import { useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { InfoHint } from "@/components/InfoHint";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Spinner } from "@/components/ui/spinner";
import { apiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { formatCalendarDate } from "@/lib/format";
import { useBooks } from "./BooksProvider";
import { accountLabel, bankAccountLabel, BooksScreen, useBankAccounts, useChart } from "./shared";
import type { Book, ControlAccounts, ReportingBasis } from "./types";

const CONTROL_LABELS: Array<[keyof ControlAccounts, string]> = [
  ["accountsReceivableLedgerAccountId", "Accounts receivable"],
  ["accountsPayableLedgerAccountId", "Accounts payable"],
  ["vatLedgerAccountId", "VAT"],
  ["uncategorisedLedgerAccountId", "Uncategorised"],
  ["retainedEarningsLedgerAccountId", "Retained earnings"],
  ["openingBalanceDifferenceLedgerAccountId", "Opening-balance difference"],
  ["stripeClearingLedgerAccountId", "Stripe clearing"],
  ["stripeFeesLedgerAccountId", "Stripe fees"],
];

export function BooksSettings() {
  return (
    <BooksScreen title="Books settings" size="narrow">
      {(book, reload) => <SettingsBody book={book} reload={reload} />}
    </BooksScreen>
  );
}

function SettingsBody({ book, reload }: { book: Book; reload: () => Promise<void> }) {
  const { canManage, canClose } = useBooks();
  const chart = useChart();
  const banks = useBankAccounts();
  const names = new Map((chart.data ?? []).map((a) => [a.id, accountLabel(a)]));
  const bankNames = new Map(banks.accounts.map((a) => [a.id, bankAccountLabel(a)]));
  const editable = canManage && book.status === "Active";

  return (
    <div className="space-y-4">
      <CloseDateCard book={book} editable={editable} reload={reload} />
      <ReportingBasisCard book={book} editable={canManage && book.status !== "Closed"} reload={reload} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            Control accounts
            {book.status !== "Setup" ? <Lock className="size-3.5 text-muted-foreground" /> : null}
            <InfoHint label="Why they are locked">
              Past entries stay consistent with the accounts they used. To change one, close these books and start new ones.
            </InfoHint>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y text-sm">
            {CONTROL_LABELS.map(([key, label]) => {
              const id = book.controlAccounts?.[key];
              return (
                <div key={key} className="flex justify-between gap-4 py-2">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="text-right font-medium">{id ? (names.get(id) ?? "Account not in the chart") : "None"}</dd>
                </div>
              );
            })}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bank accounts</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y text-sm">
            {(book.bankAccounts ?? []).map((a) => (
              <div key={a.ledgerAccountId} className="flex justify-between gap-4 py-2">
                <dt className="font-medium">{a.code ? `${a.code} · ${a.name}` : a.name}</dt>
                <dd className="text-right text-muted-foreground">
                  {a.tracked && a.bankAccountId ? (bankNames.get(a.bankAccountId) ?? "Connected account") : "Not tracked"}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {editable && canClose ? <CloseBookCard reload={reload} /> : null}
    </div>
  );
}

function CloseDateCard({ book, editable, reload }: { book: Book; editable: boolean; reload: () => Promise<void> }) {
  const { books } = useBooks();
  const [value, setValue] = useState<string | null>(book.closeDate ?? null);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    const { error } = await books.PATCH("/v1/books/current", { body: value ? { closeDate: value } : { clearCloseDate: true } });
    setSaving(false);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't save the close date."));
      return;
    }
    toast.success(value ? `Books closed up to ${formatCalendarDate(value)}.` : "Close date cleared.");
    await reload();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          Close date
          <InfoHint label="About the close date">
            Locks finished months. Anything dated on or before it posts on the next open day instead, keeping its own date on the
            entry.
          </InfoHint>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {editable ? (
          <div className="flex flex-col gap-3 @lg:flex-row @lg:items-center">
            <div className="@lg:w-56">
              <DatePicker value={value} onChange={setValue} placeholder="No close date" />
            </div>
            <Button onClick={save} disabled={saving || value === (book.closeDate ?? null)}>
              {saving ? <Spinner className="size-4" /> : null} Save
            </Button>
          </div>
        ) : (
          <p className="text-sm">
            {book.closeDate ? `Closed up to ${formatCalendarDate(book.closeDate)}.` : "No close date set."}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const BASIS_OPTIONS: Array<{ value: ReportingBasis; label: string; hint: string }> = [
  { value: "Accrual", label: "Accrual", hint: "Sales when invoiced, costs when the bill is approved." },
  { value: "Cash", label: "Cash", hint: "Sales and costs when paid, VAT included, as HMRC's cash basis and VAT cash accounting count them." },
];

function ReportingBasisCard({ book, editable, reload }: { book: Book; editable: boolean; reload: () => Promise<void> }) {
  const { books } = useBooks();
  const [saving, setSaving] = useState<ReportingBasis | null>(null);
  const current = book.reportingBasis ?? "Accrual";

  async function choose(basis: ReportingBasis) {
    if (basis === current || saving) return;
    setSaving(basis);
    const { error } = await books.PATCH("/v1/books/current", { body: { reportingBasis: basis } });
    setSaving(null);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't save the reporting basis."));
      return;
    }
    toast.success(`Reports now open on ${basis.toLowerCase()} basis.`);
    await reload();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          Reporting basis
          <InfoHint label="About the reporting basis">
            The basis every report opens on. Anyone can switch a report between accrual and cash beside the period; this only
            sets where it starts.
          </InfoHint>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {editable ? (
          <div role="radiogroup" aria-label="Default reporting basis" className="grid gap-2 @lg:grid-cols-2">
            {BASIS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={current === o.value}
                disabled={saving !== null}
                onClick={() => choose(o.value)}
                className={cn(
                  "rounded-lg border p-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
                  current === o.value ? "border-primary bg-primary/[0.05] dark:bg-primary/[0.08]" : "hover:bg-muted/40",
                )}
              >
                <span className="flex items-center gap-2 font-medium">
                  {o.label}
                  {saving === o.value ? <Spinner className="size-3.5" /> : null}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">{o.hint}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm">Reports open on {current.toLowerCase()} basis.</p>
        )}
      </CardContent>
    </Card>
  );
}

function CloseBookCard({ reload }: { reload: () => Promise<void> }) {
  const { books } = useBooks();
  const [closing, setClosing] = useState(false);

  async function close() {
    setClosing(true);
    const { error } = await books.POST("/v1/books/current/close", {});
    setClosing(false);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't close the books."));
      return;
    }
    toast.success("Books closed. They are read-only from now on.");
    await reload();
  }

  return (
    <Card className="border-destructive/30">
      <CardHeader>
        <CardTitle className="text-base">Close these books</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Closed books are read-only and take no more postings. You can then start new books, with new control accounts and a new
          start date.
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" disabled={closing}>
              {closing ? <Spinner className="size-4" /> : null} Close books
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Close these books?</AlertDialogTitle>
              <AlertDialogDescription>
                Nothing more will post to them and they can't be reopened. Bank transactions can no longer be categorised here.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep them open</AlertDialogCancel>
              <AlertDialogAction onClick={close}>Close books</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
